import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../ui/Toast';

const CRITERIA = [
  { key: 'force', label: 'Force', mandatory: true },
  { key: 'coherence', label: 'Cohérence', mandatory: true },
  { key: 'specificite', label: 'Spécificité', mandatory: true },
  { key: 'temporalite', label: 'Temporalité', mandatory: true },
  { key: 'gradient', label: 'Gradient', mandatory: true },
  { key: 'plausibilite', label: 'Plausibilité', mandatory: false },
  { key: 'experimentation', label: 'Expérimentation', mandatory: false },
  { key: 'analogie', label: 'Analogie', mandatory: false },
  { key: 'reversibilite', label: 'Réversibilité', mandatory: false },
];

const RHBradfordPanel = ({ studyId, scores = [], onSave, readOnly = false }) => {
  const [localScores, setLocalScores] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (scores && scores.length > 0) {
      setLocalScores(scores);
    } else {
      setLocalScores(CRITERIA.map(c => ({
        criterion: c.key,
        satisfied: false,
        score: 0,
        justification: '',
        is_mandatory: c.mandatory,
      })));
    }
  }, [scores, studyId]);

  const satisfiedCount = localScores.filter(s => s.satisfied).length;
  const mandatoryCount = localScores.filter(s => s.is_mandatory && s.satisfied).length;
  const isAttributable = mandatoryCount === 5;

  const handleSatisfiedChange = (index) => {
    const updated = [...localScores];
    updated[index].satisfied = !updated[index].satisfied;
    setLocalScores(updated);
    setError(null); setSuccess(false);
  };

  const handleScoreChange = (index, value) => {
    const updated = [...localScores];
    updated[index].score = parseFloat(value) || 0;
    setLocalScores(updated);
    setError(null); setSuccess(false);
  };

  const handleJustificationChange = (index, value) => {
    const updated = [...localScores];
    updated[index].justification = value;
    setLocalScores(updated);
    setError(null); setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(false);

    const missing = localScores.filter(s => s.satisfied && !(s.justification || '').trim());
    if (missing.length > 0) {
      setError('Justification requise pour tous les critères satisfaits.');
      setSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('oracle_token');
      await axios.post(`/api/rh/studies/${studyId}/bradford`, {
        scores: localScores.map(s => ({
          criterion: s.criterion, satisfied: s.satisfied,
          score: s.score, justification: s.justification,
        })),
      }, { headers: { Authorization: `Bearer ${token}` } });

      setSuccess(true);
      toast.success('Scores Bradford-Hill sauvegardés');
      if (onSave) onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde.');
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return '#22c55e';
    if (score >= 0.6) return '#f59e0b';
    if (score >= 0.3) return '#ef4444';
    return 'var(--text-muted)';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', borderRadius: '12px',
        background: 'var(--card-bg)', border: '1px solid var(--card-border)',
      }}>
        <h2 style={{ fontSize: '17px', fontWeight: '700', color: 'var(--text)', margin: 0 }}>
          Critères Bradford-Hill
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            <strong>{satisfiedCount}</strong>/9 satisfaits · <strong>{mandatoryCount}</strong>/5 obligatoires
          </span>
          <span style={{
            padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
            background: isAttributable ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)',
            color: isAttributable ? '#22c55e' : '#f59e0b',
            border: `1px solid ${isAttributable ? 'rgba(34,197,94,0.3)' : 'rgba(245,158,11,0.3)'}`,
          }}>
            {isAttributable ? 'Attribuable' : 'Insuffisant'}
          </span>
        </div>
      </div>

      {/* Alertes */}
      {error && (
        <div style={{
          padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444',
        }}>{error}</div>
      )}
      {success && (
        <div style={{
          padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
          background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', color: '#22c55e',
        }}>Scores sauvegardés avec succès.</div>
      )}

      {/* Critères */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {localScores.map((item, index) => {
          const criterion = CRITERIA.find(c => c.key === item.criterion);
          if (!criterion) return null;
          const isMandatory = criterion.mandatory;
          const scoreColor = getScoreColor(item.score);

          return (
            <div
              key={item.criterion}
              style={{
                display: 'flex', alignItems: 'center', gap: '16px',
                padding: '12px 16px', borderRadius: '10px',
                background: isMandatory ? 'rgba(37,99,235,0.04)' : 'var(--card-bg)',
                border: `1px solid ${isMandatory ? 'rgba(37,99,235,0.15)' : 'var(--card-border)'}`,
                transition: 'background 0.15s',
              }}
            >
              {/* Label + Badge */}
              <div style={{ width: '180px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>
                  {criterion.label}
                </span>
                <span style={{
                  padding: '2px 7px', borderRadius: '10px', fontSize: '9px', fontWeight: '700',
                  textTransform: 'uppercase', letterSpacing: '0.3px',
                  background: isMandatory ? 'rgba(37,99,235,0.12)' : 'rgba(139,92,246,0.12)',
                  color: isMandatory ? '#3b82f6' : '#8b5cf6',
                  border: `1px solid ${isMandatory ? 'rgba(37,99,235,0.25)' : 'rgba(139,92,246,0.25)'}`,
                }}>
                  {isMandatory ? 'Obligatoire' : 'Renforçant'}
                </span>
              </div>

              {/* Checkbox Satisfait */}
              <label style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                cursor: readOnly ? 'default' : 'pointer', flexShrink: 0, width: '90px',
              }}>
                <input
                  type="checkbox"
                  checked={item.satisfied}
                  onChange={() => handleSatisfiedChange(index)}
                  disabled={readOnly}
                  style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: readOnly ? 'default' : 'pointer' }}
                />
                <span style={{ fontSize: '12px', color: item.satisfied ? '#22c55e' : 'var(--text-muted)' }}>
                  {item.satisfied ? 'Satisfait' : 'Non'}
                </span>
              </label>

              {/* Score */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '160px', flexShrink: 0 }}>
                <input
                  type="range"
                  min="0" max="1" step="0.05"
                  value={item.score}
                  onChange={(e) => handleScoreChange(index, e.target.value)}
                  disabled={readOnly}
                  style={{
                    flex: 1, height: '4px', accentColor: scoreColor,
                    cursor: readOnly ? 'default' : 'pointer',
                    opacity: readOnly ? 0.6 : 1,
                  }}
                />
                <span style={{
                  fontSize: '14px', fontWeight: '700', color: scoreColor,
                  minWidth: '32px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                }}>
                  {Number(item.score).toFixed(2)}
                </span>
              </div>

              {/* Justification */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <input
                  type="text"
                  placeholder="Justification..."
                  value={item.justification || ''}
                  onChange={(e) => handleJustificationChange(index, e.target.value)}
                  disabled={readOnly}
                  style={{
                    width: '100%', padding: '6px 10px', borderRadius: '6px',
                    border: item.satisfied && !(item.justification || '').trim()
                      ? '1px solid rgba(37,99,235,0.4)'
                      : '1px solid var(--input-border)',
                    background: 'var(--input-bg)', color: 'var(--text)',
                    fontSize: '12px', fontFamily: 'inherit', outline: 'none',
                    opacity: readOnly ? 0.6 : 1,
                  }}
                  onFocus={e => e.currentTarget.style.borderColor = '#3b82f6'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--input-border)'}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Bouton sauvegarder */}
      {!readOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 24px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
              border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
              background: saving ? 'var(--card-border)' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: 'white', transition: 'all 0.2s',
              boxShadow: saving ? 'none' : '0 2px 8px rgba(37,99,235,0.3)',
            }}
            onMouseEnter={e => { if (!saving) e.currentTarget.style.transform = 'scale(1.02)'; }}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer les scores'}
          </button>
        </div>
      )}
    </div>
  );
};

export default RHBradfordPanel;
